/**
 * IANA timezone list for the signup and profile forms.
 *
 * `Intl.supportedValuesOf('timeZone')` gives the browser's full list, which is
 * the right answer where it exists (Chrome 99+, Safari 15.4+, Firefox 93+).
 * It is accessed defensively because a self-hosted app gets opened on older
 * devices, and a signup form that throws is worse than a shorter list.
 *
 * A profile's timezone is not cosmetic: it is what "08:00" means for every dose
 * (CONTRACT §2). Getting it wrong shifts a whole regimen, so the field is
 * editable rather than silently detected.
 */

/** Detect the browser's zone. Falls back to UTC, never to an empty string. */
export function detectTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof tz === 'string' && tz.length > 0) return tz;
  } catch {
    /* fall through */
  }
  return 'UTC';
}

/** True when the runtime recognises this zone. */
export function isValidTimezone(tz: string): boolean {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * A short curated list, used as the fallback when the browser cannot enumerate
 * zones. Covers the common cases without pretending to be exhaustive.
 */
const FALLBACK_TIMEZONES: readonly string[] = [
  'UTC',
  'America/Anchorage',
  'America/Bogota',
  'America/Chicago',
  'America/Denver',
  'America/Halifax',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/New_York',
  'America/Phoenix',
  'America/Sao_Paulo',
  'America/Toronto',
  'America/Vancouver',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Jakarta',
  'Asia/Jerusalem',
  'Asia/Kolkata',
  'Asia/Karachi',
  'Asia/Manila',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Athens',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Copenhagen',
  'Europe/Dublin',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Moscow',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Prague',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Vienna',
  'Europe/Warsaw',
  'Europe/Zurich',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Pacific/Honolulu',
];

let cached: string[] | null = null;

/** Every zone the runtime knows, sorted, always including the browser's own. */
export function listTimezones(): string[] {
  if (cached) return cached;

  let zones: string[] = [];
  try {
    // Not in every lib.dom.d.ts version, hence the cast rather than a direct call.
    const supportedValuesOf = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf;
    if (typeof supportedValuesOf === 'function') {
      zones = supportedValuesOf('timeZone');
    }
  } catch {
    zones = [];
  }

  if (!Array.isArray(zones) || zones.length === 0) {
    zones = [...FALLBACK_TIMEZONES];
  }

  const detected = detectTimezone();
  const set = new Set(zones);
  set.add(detected);
  set.add('UTC');

  cached = [...set].sort((a, b) => a.localeCompare(b));
  return cached;
}

/** "America/New_York" -> "America / New York (GMT-4)". */
export function formatTimezoneLabel(tz: string, now: Date = new Date()): string {
  const pretty = tz.replace(/_/g, ' ').replace(/\//g, ' / ');
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(now);
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value;
    return offset ? `${pretty} (${offset})` : pretty;
  } catch {
    return pretty;
  }
}

/** Options ready for <Select>. */
export function timezoneOptions(): { value: string; label: string }[] {
  return listTimezones().map((tz) => ({ value: tz, label: formatTimezoneLabel(tz) }));
}
